/**
 * Utility functions for handling image URLs
 */

// Remove /api suffix from backend URL since static files are served from root
const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');

/**
 * Get the full URL for an image
 * @param {string|object} imagePath - The image path (relative or absolute) or image object with url property
 * @returns {string} - The full URL for the image
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) return null;

  // Handle image objects with url property (ProductCard format)
  if (typeof imagePath === 'object' && imagePath.url) {
    return getImageUrl(imagePath.url);
  }

  // Ensure it's a string
  if (typeof imagePath !== 'string') return null;

  // If it's already a full URL (starts with http/https), return as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }

  // If it starts with /uploads, prepend backend URL
  if (imagePath.startsWith('/uploads/')) {
    return `${BACKEND_URL}${imagePath}`;
  }

  // If it's a relative path, prepend backend URL
  if (imagePath.startsWith('/')) {
    return `${BACKEND_URL}${imagePath}`;
  }

  // If it doesn't start with /, assume it's a relative path
  return `${BACKEND_URL}/${imagePath}`;
};

/**
 * Get the primary image URL for a product
 * @param {Object} product - The product object
 * @returns {string|null} - The primary image URL or null
 */
export const getPrimaryImageUrl = (product) => {
  if (!product) return null;

  // Check for images array (object format with url property)
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    // Find primary image or use first
    const primaryImg = product.images.find(img => img?.isPrimary);
    const firstImg = primaryImg || product.images[0];
    if (firstImg) {
      // Handle both object format {url: '...'} and string format
      const imgPath = typeof firstImg === 'object' ? firstImg.url : firstImg;
      if (imgPath) return getImageUrl(imgPath);
    }
  }

  // Fallback to direct image fields
  const imagePath = product.primaryImage || product.imageUrl || product.image;
  return getImageUrl(imagePath);
};

/**
 * Get all image URLs for a product (primary + gallery)
 * @param {Object} product - The product object
 * @returns {Array} - Array of image URLs
 */
export const getAllImageUrls = (product) => {
  if (!product) return [];

  const images = [];

  // Check if backend already provided an images array
  // Handles both object format [{url: '...', isPrimary: true}] and string format ['path1', 'path2']
  if (product.images && Array.isArray(product.images) && product.images.length > 0) {
    // Sort to put primary image first if in object format
    const sortedImages = [...product.images].sort((a, b) => {
      if (typeof a === 'object' && a?.isPrimary) return -1;
      if (typeof b === 'object' && b?.isPrimary) return 1;
      return 0;
    });

    sortedImages.forEach(img => {
      // Handle both object format {url: '...'} and string format
      const imagePath = typeof img === 'object' ? img?.url : img;
      const imageUrl = getImageUrl(imagePath);
      if (imageUrl && !images.includes(imageUrl)) {
        images.push(imageUrl);
      }
    });
  }

  // If no images from array, try primary image
  if (images.length === 0) {
    const primaryImage = getPrimaryImageUrl(product);
    if (primaryImage) {
      images.push(primaryImage);
    }
  }

  // Add gallery images if not already included
  if (product.gallery && Array.isArray(product.gallery)) {
    product.gallery.forEach(img => {
      const imagePath = typeof img === 'object' ? img?.url : img;
      const imageUrl = getImageUrl(imagePath);
      if (imageUrl && !images.includes(imageUrl)) {
        images.push(imageUrl);
      }
    });
  }

  // Also check for 'image' field (single image)
  if (images.length === 0 && product.image) {
    const imageUrl = getImageUrl(product.image);
    if (imageUrl) {
      images.push(imageUrl);
    }
  }

  // If no images found, add placeholder
  if (images.length === 0) {
    images.push('/placeholder-product.svg');
  }

  return images;
};

/**
 * Get a fallback image URL
 * @returns {string} - The fallback image URL
 */
export const getFallbackImageUrl = () => {
  return '/placeholder-product.svg';
};
